/**
 * Global Property Selector Component
 * 
 * Allows users to select properties from global property definitions
 * with dependency preview and management
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Input,
  Select,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Spin,
  Alert,
  Checkbox,
  message,
  Form
} from 'antd';
import {
  SearchOutlined,
  InfoCircleOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import { GlobalPropertiesService } from '../../services/api/globalPropertiesService';
import {
  PropertyDefinition,
  PropertyDefinitionResponse
} from '../../types/globalProperties';

const { Option } = Select;
const { Text } = Typography;

interface GlobalPropertySelectorProps {
  visible: boolean;
  templateId?: string; // Made optional to support both templates and artifacts
  artifactId?: string; // Optional artifactId for context
  onCancel: () => void;
  onSelect: (propertyKeys: string[], includeDependencies: boolean) => Promise<void>;
  excludeProperties?: string[]; // Properties already added
  context?: 'template' | 'artifact'; // Context for UI text customization
}

interface PropertyListItem {
  property_key: string;
  property_type: string;
  description?: string;
}

interface DependencyPreview {
  property_key: string;
  property_type: string;
  description?: string;
  dependencies: string[];
  dependency_count: number;
}

export const GlobalPropertySelector: React.FC<GlobalPropertySelectorProps> = ({
  visible,
  templateId,
  artifactId,
  onCancel,
  onSelect,
  excludeProperties = [],
  context = templateId ? 'template' : 'artifact'
}) => {
  const [searchText, setSearchText] = useState('');
  const [propertyTypeFilter, setPropertyTypeFilter] = useState<string | undefined>();
  const [properties, setProperties] = useState<PropertyListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState<Set<string>>(new Set());
  const [dependencyPreview, setDependencyPreview] = useState<DependencyPreview | null>(null);
  const [loadingDependencies, setLoadingDependencies] = useState(false);
  const [includeDependencies, setIncludeDependencies] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCreatePropertyDialog, setShowCreatePropertyDialog] = useState(false);
  const [createPropertyForm] = Form.useForm();
  const [creatingProperty, setCreatingProperty] = useState(false);

  const loadProperties = useCallback(async () => {
    setLoading(true);
    try {
      // Use the standard properties endpoint - doesn't require templateId
      const params: any = { limit: 100 };
      if (searchText) {
        params.search = searchText;
      }
      if (propertyTypeFilter) {
        params.property_type = propertyTypeFilter;
      }
      
      // Use GlobalPropertiesService which uses the working /properties endpoint
      // Use summary=false to get full PropertyDefinitionResponse format
      const response = await GlobalPropertiesService.listPropertyDefinitions({
        ...params,
        summary: false
      });
      
      // Type guard: ensure we have PropertyDefinitionResponse format
      const propertyDefinitions = response.properties as PropertyDefinitionResponse[];
      
      // Transform PropertyDefinitionResponse to PropertyListItem format
      const propertyList: PropertyListItem[] = propertyDefinitions.map((prop) => ({
        property_key: prop.property_key,
        property_type: prop.definition?.property_type || 'string',
        description: prop.definition?.description
      }));
      
      // Filter out already added properties
      const available = propertyList.filter((prop: PropertyListItem) => 
        !excludeProperties.includes(prop.property_key)
      );
      
      setProperties(available);
    } catch (error: any) {
      console.error('Error loading properties:', error);
      message.error('Failed to load global properties');
    } finally {
      setLoading(false);
    }
  }, [searchText, propertyTypeFilter, excludeProperties]);

  useEffect(() => {
    if (visible) {
      loadProperties();
    }
  }, [visible, loadProperties]);

  const handlePropertyToggle = (propertyKey: string) => {
    setSelectedProperties(prev => {
      const newSet = new Set(prev);
      const wasSelected = newSet.has(propertyKey);
      if (wasSelected) {
        newSet.delete(propertyKey);
      } else {
        newSet.add(propertyKey);
      }
      
      // Try to load dependencies when selecting a property (optional, graceful failure)
      if (!wasSelected && newSet.size === 1) {
        // Load preview for the first selected property
        loadDependencyPreview(propertyKey).catch(() => {
          // Silently fail - dependency preview is optional
        });
      } else if (wasSelected && newSet.size === 0) {
        // Clear preview when no properties selected
        setDependencyPreview(null);
      }
      
      return newSet;
    });
  };

  const loadDependencyPreview = async (propertyKey: string) => {
    // Only load dependency preview if templateId is provided (template context)
    if (!templateId) {
      return;
    }
    
    setLoadingDependencies(true);
    
    try {
      const response = await apiClient.get(
        `/state-templates/${templateId}/property-dependencies/${propertyKey}`
      );
      
      setDependencyPreview({
        property_key: response.data.property_key,
        property_type: response.data.property_type,
        description: response.data.description,
        dependencies: response.data.dependencies || [],
        dependency_count: response.data.dependency_count || 0
      });
    } catch (error: any) {
      // Gracefully handle missing endpoint - dependency preview is optional
      if (error.response?.status === 404) {
        console.log('Dependency endpoint not available, skipping preview');
        setDependencyPreview(null);
      } else {
        console.error('Error loading dependencies:', error);
        setDependencyPreview(null);
      }
    } finally {
      setLoadingDependencies(false);
    }
  };

  const handleCreateProperty = async () => {
    try {
      const values = await createPropertyForm.validateFields();
      const propertyKey = values.property_key.trim();

      if (!propertyKey) {
        message.error('Property key is required');
        return;
      }

      if (!/^[a-z0-9_]+$/.test(propertyKey)) {
        message.error('Property key must be lowercase alphanumeric with underscores');
        return;
      }

      setCreatingProperty(true);

      const propertyDefinition: PropertyDefinition = {
        property_key: propertyKey,
        property_type: values.property_type || 'string', // Use selected type or default to string
        input_source: 'user_input',
      };

      await GlobalPropertiesService.createPropertyDefinition(propertyDefinition);
      message.success(`Property '${propertyKey}' created successfully`);
      createPropertyForm.resetFields();
      setShowCreatePropertyDialog(false);

      // Refresh properties list and auto-select the newly created property
      await loadProperties();
      setSelectedProperties(new Set([propertyKey]));
    } catch (error: any) {
      console.error('Error creating property:', error);
      message.error(`Failed to create property: ${error.message || 'Unknown error'}`);
    } finally {
      setCreatingProperty(false);
    }
  };

  const handleConfirm = async () => {
    if (selectedProperties.size === 0) return;
    
    setSubmitting(true);
    try {
      const propertyKeys = Array.from(selectedProperties);
      await onSelect(propertyKeys, includeDependencies);
      // onSelect callback handles success/error messages
      setSelectedProperties(new Set());
      setDependencyPreview(null);
      void loadProperties();
    } catch (error: any) {
      console.error('Error adding properties:', error);
      // Error message is handled by onSelect callback, but we can show a generic one here if needed
      // The error is re-thrown from onSelect, so this is just a fallback
      void message.error(error.response?.data?.detail || 'Failed to add properties');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Property Key',
      dataIndex: 'property_key',
      key: 'property_key',
      render: (text: string) => <strong>{text}</strong>
    },
    {
      title: 'Type',
      dataIndex: 'property_type',
      key: 'property_type',
      render: (type: string) => <Tag>{type}</Tag>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Select',
      key: 'select',
      width: 100,
      render: (_: any, record: PropertyListItem) => (
        <Checkbox
          checked={selectedProperties.has(record.property_key)}
          onChange={() => handlePropertyToggle(record.property_key)}
        />
      )
    }
  ];

  const modalTitle = context === 'template' ? 'Select Global Property' : 'Select Global Property';

  return (
    <>
      <Modal
        title={modalTitle}
        open={visible}
        onCancel={onCancel}
        width={900}
        footer={[
          <Button key="cancel" onClick={onCancel}>
            Cancel
          </Button>,
          <Button
            key="create"
            icon={<PlusOutlined />}
            onClick={() => setShowCreatePropertyDialog(true)}
          >
            Create New Property
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={handleConfirm}
            disabled={selectedProperties.size === 0}
            loading={submitting}
          >
            Add {selectedProperties.size > 0 ? `${selectedProperties.size} ` : ''}Propert{selectedProperties.size === 1 ? 'y' : 'ies'}
          </Button>
        ]}
      >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Search and Filter */}
        <Space>
          <Input
            placeholder="Search properties..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            placeholder="Filter by type"
            value={propertyTypeFilter}
            onChange={setPropertyTypeFilter}
            allowClear
            style={{ width: 150 }}
          >
            <Option value="string">String</Option>
            <Option value="number">Number</Option>
            <Option value="boolean">Boolean</Option>
            <Option value="date">Date</Option>
            <Option value="currency">Currency</Option>
            <Option value="duration">Duration</Option>
            <Option value="url">URL</Option>
            <Option value="uuid">UUID</Option>
            <Option value="list">List</Option>
          </Select>
        </Space>

        {/* Properties Table */}
        <Table
          dataSource={properties}
          columns={columns}
          loading={loading}
          rowKey="property_key"
          pagination={{ pageSize: 10 }}
          size="small"
        />

        {/* Selection Summary */}
        {selectedProperties.size > 0 && (
          <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Selected Properties ({selectedProperties.size}): </Text>
                <Space wrap style={{ marginTop: 8 }}>
                  {Array.from(selectedProperties).map((key) => (
                    <Tag 
                      key={key} 
                      color="blue"
                      closable
                      onClose={() => handlePropertyToggle(key)}
                    >
                      {key}
                    </Tag>
                  ))}
                </Space>
              </div>
              
              {/* Optional dependency preview for first selected property */}
              {dependencyPreview && selectedProperties.size === 1 && (
                <>
                  {dependencyPreview.dependency_count > 0 && (
                    <Alert
                      message={
                        <span>
                          Selected property has <strong>{dependencyPreview.dependency_count}</strong> related 
                          property{dependencyPreview.dependency_count !== 1 ? 'ies' : ''} 
                          {' '}that can be automatically included
                        </span>
                      }
                      type="info"
                      icon={<InfoCircleOutlined />}
                      style={{ marginTop: 8 }}
                    />
                  )}
                  
                  <Checkbox
                    checked={includeDependencies}
                    onChange={(e) => setIncludeDependencies(e.target.checked)}
                    style={{ marginTop: 8 }}
                  >
                    Automatically include related properties (if available)
                  </Checkbox>
                </>
              )}
            </Space>
          </div>
        )}
      </Space>
    </Modal>

    {/* Create New Property Dialog */}
    <Modal
      title="Create New Global Property"
      open={showCreatePropertyDialog}
      onCancel={() => {
        setShowCreatePropertyDialog(false);
        createPropertyForm.resetFields();
      }}
      onOk={handleCreateProperty}
      okText="Create"
      cancelText="Cancel"
      confirmLoading={creatingProperty}
    >
      <Form
        form={createPropertyForm}
        layout="vertical"
        initialValues={{
          property_type: 'string'
        }}
      >
        <Form.Item
          name="property_key"
          label="Property Key"
          rules={[
            { required: true, message: 'Property key is required' },
            { pattern: /^[a-z0-9_]+$/, message: 'Property key must be lowercase alphanumeric with underscores' }
          ]}
        >
          <Input 
            placeholder="e.g., revenue, customer_name, order_status" 
            autoFocus
          />
        </Form.Item>
        <Form.Item
          name="property_type"
          label="Property Type"
          rules={[{ required: true, message: 'Property type is required' }]}
        >
          <Select placeholder="Select property type">
            <Option value="string">String</Option>
            <Option value="number">Number</Option>
            <Option value="boolean">Boolean</Option>
            <Option value="date">Date</Option>
            <Option value="date_range">Date Range</Option>
            <Option value="currency">Currency</Option>
            <Option value="duration">Duration</Option>
            <Option value="url">URL</Option>
            <Option value="uuid">UUID</Option>
            <Option value="uuid[]">UUID[]</Option>
            <Option value="list">List</Option>
          </Select>
        </Form.Item>
        <Alert
          message="A minimal global property will be created with the selected type and default input_source: user_input. You can edit it later in the Global Properties Management page."
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Form>
    </Modal>
    </>
  );
};

export default GlobalPropertySelector;
